import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import HeaderNavbar from '../components/HeaderNavbar';
import Footer from '../components/Footer';
import Spinner from 'react-bootstrap/Spinner';
import config from '../config';
import api from '../lib/request';
import { useAuthContext } from '../contexts/AuthContext';
import './Login.css';

const initialForm = {
  organizationId: '',
  organizationName: '',
  title: '',
  subTitle: '',
  description: '',
  currentProjectUrl: '',
  file: null
};

function Publish() {
  const { isAuthenticated, isAuthReady } = useAuthContext();
  const [organizations, setOrganizations] = useState([]);
  const [orgMode, setOrgMode] = useState('existing');
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const hasOrganizations = useMemo(() => organizations.length > 0, [organizations]);

  const gobackPath = window.encodeURI('/publish');

  useEffect(() => {
    if (!isAuthenticated) return;
    let mounted = true;

    async function fetchOrganizations() {
      setLoadingOrgs(true);
      try {
        const response = await api.get('/api/v1/organizations/mine');
        if (!mounted) return;
        const orgs = Array.isArray(response?.organizations) ? response.organizations : [];
        setOrganizations(orgs);
        if (orgs.length) {
          setOrgMode('existing');
          setForm((prev) => ({
            ...prev,
            organizationId: prev.organizationId || String(orgs[0].id)
          }));
        } else {
          setOrgMode('new');
          setForm((prev) => ({ ...prev, organizationId: '' }));
        }
      } catch (e) {
        if (!mounted) return;
        setError('Unable to load your organizations. Please try again later.');
      } finally {
        if (mounted) {
          setLoadingOrgs(false);
        }
      }
    }

    fetchOrganizations();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  if (!isAuthReady) {
    return (
      <>
        <HeaderNavbar />
        <div className="row">
          <div className="Auth-form-container">
            <Spinner animation="border" />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!isAuthenticated) {
    return (<Navigate to={`${config.prefixPath}/login?goback=${gobackPath}&rl=1`} />);
  }

  const handleInputChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setForm((prev) => ({
      ...prev,
      file
    }));
  };

  const handleOrgModeChange = (event) => {
    const mode = event.target.value;
    setOrgMode(mode);
    setError('');
    if (mode === 'existing' && organizations.length && !form.organizationId) {
      setForm((prev) => ({ ...prev, organizationId: String(organizations[0].id) }));
    }
  };

  const validateForm = () => {
    if (orgMode === 'existing') {
      if (!form.organizationId) {
        return 'Select one of your organizations or create a new one.';
      }
    } else {
      if (!form.organizationName.trim()) {
        return 'Organization name is required.';
      }
    }

    if (!form.title.trim()) {
      return 'Project title is required.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('organizationMode', orgMode);
      if (orgMode === 'existing') {
        formData.append('organizationId', form.organizationId);
      } else {
        formData.append('organizationName', form.organizationName.trim());
      }
      formData.append('title', form.title.trim());
      formData.append('subTitle', form.subTitle.trim());
      formData.append('description', form.description.trim());
      formData.append('currentProjectUrl', form.currentProjectUrl.trim());
      if (form.file) {
        formData.append('images', form.file);
      }

      const response = await api({
        method: 'post',
        url: '/api/v1/projects/requests',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response?.ok) {
        setSuccessMessage('Your Project request has been submitted. We will notify you once it is approved.');
        setForm(initialForm);
        if (!hasOrganizations) {
          setOrgMode('new');
        }
      } else {
        setError(response?.message || 'Unable to submit your Project request.');
      }
    } catch (e) {
      setError(e?.message || 'Unable to submit your Project request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <HeaderNavbar />
      <div className="row">
        <div className="Auth-form-container">
          <form className="Auth-form" onSubmit={handleSubmit} style={{ width: 520, maxWidth: '92vw' }}>
            <div className="Auth-form-content">
              <h3 className="Auth-form-title">Create your Project</h3>

              {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
              {successMessage ? (
                <Alert variant="success" className="mt-3">{successMessage}</Alert>
              ) : (
                <>
                  <div className="mt-4">
                    <label className="form-label">Organization</label>
                    {hasOrganizations ? (
                      <>
                        <div className="d-flex gap-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="orgMode"
                              id="org-existing"
                              value="existing"
                              checked={orgMode === 'existing'}
                              onChange={handleOrgModeChange}
                            />
                            <label className="form-check-label" htmlFor="org-existing">
                              Use existing
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="orgMode"
                              id="org-new"
                              value="new"
                              checked={orgMode === 'new'}
                              onChange={handleOrgModeChange}
                            />
                            <label className="form-check-label" htmlFor="org-new">
                              Create new
                            </label>
                          </div>
                        </div>

                        {loadingOrgs ? (
                          <div className="mt-2"><Spinner animation="border" size="sm" /></div>
                        ) : orgMode === 'existing' ? (
                          <div className="mt-2">
                            <select
                              className="form-control"
                              value={form.organizationId}
                              onChange={handleInputChange('organizationId')}
                            >
                              {organizations.map((org) => (
                                <option key={org.id} value={org.id}>{org.name}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Organization name"
                              value={form.organizationName}
                              onChange={handleInputChange('organizationName')}
                              required
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="mt-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Organization name"
                          value={form.organizationName}
                          onChange={handleInputChange('organizationName')}
                          required
                        />
                      </div>
                    )}
                  </div>

                  <hr className="my-4" />

                  <div className="form-group mt-3">
                    <label>Project title</label>
                    <input
                      type="text"
                      className="form-control mt-1"
                      placeholder="Project name"
                      value={form.title}
                      onChange={handleInputChange('title')}
                      required
                    />
                  </div>

                  <div className="form-group mt-3">
                    <label>Project subtitle</label>
                    <input
                      type="text"
                      className="form-control mt-1"
                      placeholder="Short tagline"
                      value={form.subTitle}
                      onChange={handleInputChange('subTitle')}
                    />
                  </div>

                  <div className="form-group mt-3">
                    <label>Project description</label>
                    <textarea
                      className="form-control mt-1"
                      placeholder="What should makers know about this project?"
                      rows={4}
                      value={form.description}
                      onChange={handleInputChange('description')}
                    />
                  </div>

                  <div className="form-group mt-3">
                    <label>Your current Project URL</label>
                    <input
                      type="url"
                      className="form-control mt-1"
                      placeholder="ie: https://github.com/your-org/your-project"
                      value={form.currentProjectUrl}
                      onChange={handleInputChange('currentProjectUrl')}
                    />
                    <small className="form-text text-muted">
                      This URL is for review only and will not be published on this site.
                    </small>
                  </div>

                  <div className="form-group mt-3">
                    <label>Project image</label>
                    <input
                      onChange={handleFileChange}
                      className="form-control mt-1"
                      name="images"
                      type="file"
                      accept="image/*;capture=camera"
                    />
                    <small className="form-text text-muted">
                      Recommended header image size: 2500 × 280 px.
                    </small>
                  </div>

                  <div className="d-grid gap-2 mt-4">
                    {submitting ? (
                      <Spinner animation="border" />
                    ) : (
                      <button type="submit" className="btn btn-primary">Submit</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Publish;
